import React, { useState, useEffect } from 'react';
import api from '../../services/api';
//import { toast } from 'react-toastify';
import toast from '../../utils/toast';
import { FaSave } from 'react-icons/fa';

const InvoiceSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    companyInfo: {
      name: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      },
      phone: '',
      email: '',
      website: ''
    },
    taxInfo: {
      gstNumber: '',
      vatNumber: '',
      taxIdentificationNumber: ''
    },
    invoiceConfig: {
      invoicePrefix: 'INV',
      proformaPrefix: 'PRO',
      dueDateDays: 30,
      currency: 'USD',
      currencySymbol: '$'
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/invoices/admin/settings');
      const settings = response.data;
      
      setFormData({
        companyInfo: {
          name: settings.companyInfo?.name || '',
          address: {
            street: settings.companyInfo?.address?.street || '',
            city: settings.companyInfo?.address?.city || '',
            state: settings.companyInfo?.address?.state || '',
            zipCode: settings.companyInfo?.address?.zipCode || '',
            country: settings.companyInfo?.address?.country || ''
          },
          phone: settings.companyInfo?.phone || '',
          email: settings.companyInfo?.email || '',
          website: settings.companyInfo?.website || ''
        },
        taxInfo: {
          gstNumber: settings.taxInfo?.gstNumber || '',
          vatNumber: settings.taxInfo?.vatNumber || '',
          taxIdentificationNumber: settings.taxInfo?.taxIdentificationNumber || ''
        },
        invoiceConfig: {
          invoicePrefix: settings.invoiceConfig?.invoicePrefix || 'INV',
          proformaPrefix: settings.invoiceConfig?.proformaPrefix || 'PRO',
          dueDateDays: settings.invoiceConfig?.dueDateDays || 30,
          currency: settings.invoiceConfig?.currency || 'USD',
          currencySymbol: settings.invoiceConfig?.currencySymbol || '$'
        }
      });
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      await api.put('/invoices/admin/settings', formData);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Invoice Settings</h1>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <FaSave className="mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Information */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Company Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
              <input
                type="text"
                value={formData.companyInfo.name}
                onChange={(e) => handleInputChange('companyInfo', 'name', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.companyInfo.email}
                onChange={(e) => handleInputChange('companyInfo', 'email', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="text"
                value={formData.companyInfo.phone}
                onChange={(e) => handleInputChange('companyInfo', 'phone', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
              <input
                type="url"
                value={formData.companyInfo.website}
                onChange={(e) => handleInputChange('companyInfo', 'website', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Tax Information */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Tax Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
              <input
                type="text"
                value={formData.taxInfo.gstNumber}
                onChange={(e) => handleInputChange('taxInfo', 'gstNumber', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number</label>
              <input
                type="text"
                value={formData.taxInfo.vatNumber}
                onChange={(e) => handleInputChange('taxInfo', 'vatNumber', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID Number</label>
              <input
                type="text"
                value={formData.taxInfo.taxIdentificationNumber}
                onChange={(e) => handleInputChange('taxInfo', 'taxIdentificationNumber', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Invoice Configuration */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Invoice Configuration</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Prefix</label>
              <input
                type="text"
                value={formData.invoiceConfig.invoicePrefix}
                onChange={(e) => handleInputChange('invoiceConfig', 'invoicePrefix', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Proforma Prefix</label>
              <input
                type="text"
                value={formData.invoiceConfig.proformaPrefix}
                onChange={(e) => handleInputChange('invoiceConfig', 'proformaPrefix', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date (Days)</label>
              <input
                type="number"
                min="1"
                value={formData.invoiceConfig.dueDateDays}
                onChange={(e) => handleInputChange('invoiceConfig', 'dueDateDays', parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default InvoiceSettings; 